<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { ddlHelper, sessionHelper } from "@/utils";
import { SESSION } from "@/constants";
import { required, email, sameAs } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from "@/i18n";
import { LOOKUP_MODULE } from '@/state/modules/lookup'
import { USER_MODULE } from '@/state/modules/user'
import { lookupMethods, userMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

// const checkFalse = (value) => value != false;

const checkPassword = (value) => {
  if (value != null && value != "") {
    return /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&])(?=.{8,20})/.test(
      value
    );
  } else {
    return true;
  }
};

export default {
  page: {
    title: i18n.t("label.createX", [i18n.t("module.user")]),
  },
  components: { Layout, PageHeader, Multiselect },
  beforeRouteLeave(to, from, next) {
    if (to.name != "User-listing") {
      sessionHelper.clearSearch(SESSION.USER_SEARCH);
    }
    next();
  },
  computed: {
    ...mapState(USER_MODULE, ["status"]),
    ...mapState(LOOKUP_MODULE),
    roleDdl() {
      return this.$store.state.lookup.roleDdl;
    },
    selectedRole: {
      get() {
        return this.roleDdl.find((role) => role.value === this.createForm.roleId) || null;
      },
      set(role) {
        this.createForm.roleId = role ? role.value : null;
      },
    },
    selectedCountries: {
      get() {
        return this.countryDdl.filter((c) => this.createForm.countryCodes.includes(c.value));
      },
      set(options) {
        this.createForm.countryCodes = (options || []).map((o) => o.value);
      },
    },
  },
  data() {
    return {
      title: i18n.t("label.createX", [i18n.t("module.user")]),
      items: [
        {
          text: i18n.t("module.dashboard"),
          href: "/",
        },
        {
          text: i18n.t("module.user"),
          href: "/user",
        },
        {
          text: i18n.t("label.createX", [i18n.t("module.user")]),
          active: true,
        },
      ],
      submitted: false,
      statusDdl: ddlHelper.getActivationStatus(false, true),
      countryDdl: ddlHelper.getAllMonitorCountries(),
      permissions: sessionHelper.getPermissions(),
      createPermission: null,
      createForm: {
        name: "",
        email: "",
        roleId: null,
        countryCodes: [],
        password: "",
        passwordConfirmation: "",
      },
    };
  },
  validations: {
    createForm: {
      name: { required },
      email: { required, email },
      roleId: { required },
      password: { checkPassword },
      passwordConfirmation: { sameAs: sameAs("password") },
    },
  },
  async created() {
    await this.validPermissions();
    this.lookupRole({ hasOptionAll: false });
  },
  methods: {
    ...userMethods,
    ...lookupMethods,
    async validPermissions() {
      var jsonPermissions = JSON.parse(this.permissions)
      if (jsonPermissions.some(p => p.code === "USER_CREATE")) {
        this.createPermission = true;
      }
      else {
        this.createPermission = false;
      }
    },
    requestCreate() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        let data = {
          name: this.createForm.name,
          email: this.createForm.email,
          roleId: this.createForm.roleId,
          countryCodes: this.createForm.countryCodes,
          password: this.createForm.password,
        };

        if (this.createPermission == true) {
          this.createUser({ data });
        }
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="requestCreate" v-if="createPermission == true">
              <b-form-group id="name" label-cols-sm="2" label-cols-lg="2" :label="$t('label.name')" label-for="name">
                <b-form-input for="name" v-model.trim="createForm.name" :class="{
                  'is-invalid': submitted &&
                    !$v.createForm.name.required
                }"></b-form-input>
                <div v-if="submitted &&
                  !$v.createForm.name.required" class="invalid-feedback">
                  <span v-if="!$v.createForm.name.required">{{
                    $t("message.requiredX", [$t("label.name")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="email" label-cols-sm="2" label-cols-lg="2" :label="$t('label.email')" label-for="email">
                <b-form-input id="email" v-model.trim="createForm.email" :class="{
                  'is-invalid':
                    submitted &&
                    (!$v.createForm.email.required ||
                      !$v.createForm.email.email),
                }"></b-form-input>
                <div v-if="submitted &&
                  (!$v.createForm.email.required ||
                    !$v.createForm.email.email)
                " class="invalid-feedback">
                  <span v-if="!$v.createForm.email.required">{{
                    $t("message.requiredX", [$t("label.email")])
                  }}</span>
                  <span v-if="!$v.createForm.email.email">{{
                    $t("message.validX", [$t("label.email")])
                  }}</span>
                </div>
              </b-form-group>

              <div class="form-group row mb-0">
                <label class="col-md-2 col-form-label">{{ $t("module.role") }}</label>
                <div class="col-md-10">
                  <multiselect v-model="selectedRole" :options="roleDdl" label="text" track-by="value"
                    :show-labels="false" :allow-empty="false"
                    :placeholder="$t('message.pleaseSelectX', [$t('module.role')])"></multiselect>
                  <div v-if="submitted && !$v.createForm.roleId.required" style="color: #ff3d60; font-size: 80%">
                    <span>{{ $t("message.requiredX", [$t("module.role")]) }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group row mb-0" style="padding-bottom: 10px">
                <label class="col-md-2 col-form-label">{{ $t('label.allowedCountries') }}</label>
                <div class="col-md-10">
                  <multiselect v-model="selectedCountries" :options="countryDdl" label="text" track-by="value"
                    :multiple="true" :close-on-select="false" :show-labels="false"
                    :placeholder="$t('label.allCountries')"></multiselect>
                  <small class="form-text text-muted">{{ $t('message.allowedCountriesHelp') }}</small>
                </div>
              </div>

              <b-form-group id="password" label-cols-sm="2" label-cols-lg="2" :label="$t('label.password')"
                label-for="password">
                <b-form-input id="password" type="password" v-model.trim="createForm.password"
                  autocomplete="new-password" :placeholder="$t('label.enter', [$t('label.password')])"
                  :class="{ 'is-invalid': !$v.createForm.password.checkPassword }"></b-form-input>
                <div v-if="!$v.createForm.password.checkPassword" class="invalid-feedback">
                  <span>{{ $t("message.passwordErrorMessage") }}</span>
                </div>
              </b-form-group>

              <b-form-group id="passwordConfirmation" label-cols-sm="2" label-cols-lg="2"
                :label="$t('label.confirmationPassword')" label-for="passwordConfirmation">
                <b-form-input id="passwordConfirmation" type="password" v-model.trim="createForm.passwordConfirmation"
                  autocomplete="new-password" :placeholder="$t('label.enter', [$t('label.confirmationPassword')])"
                  :class="{ 'is-invalid': !$v.createForm.passwordConfirmation.sameAs }"></b-form-input>
                <div v-if="!$v.createForm.passwordConfirmation.sameAs" class="invalid-feedback">
                  <span>{{ $t("message.sameAs") }}</span>
                </div>
              </b-form-group>

              <div class="mt-4 text-center" style="float: right">
                <button class="btn btn-info w-md waves-effect waves-light" style="margin-right: 10px" type="submit"
                  :disabled="status.processing">
                  {{ $t("label.createX", [$t("module.user")]) }}
                </button>
              </div>
            </form>
            <div style="text-align: center;" v-else>
              {{ $t('message.noPermissionMessage') }}
            </div>
          </div>
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
  </Layout>
</template>